// @flow
import styled from 'styled-components';
import React, { type Node } from 'react';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getMoneyFormat } from '@tectonics/look-and-feel/shopify/commons/money-format';
import { fxToCurrencyString } from '@tectonics/prs/money';
import { getReceiptFx, getReceiptAmountExt } from '@tectonics/prs/receipts/receipt';
import { getAmountCurrencyCode } from '@tectonics/prs/money/amount';

export function TooltipIOF(): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Imposto federal no Brasil cobrado sobre operações financeiras.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function TooltipIVA(): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Note que en base a la Ley N 27.430 se agregará un 21% de IVA al valor de esta compra.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function TooltipIP(): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Según la ley 27541 y el Decreto 99/2019, puede incluir un porcentaje de 8% o 30% sobre el valor de la transacción en pesos, dependiendo del tipo de bienes / servicios adquiridos.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function TooltipIGIBP({IGIBPPercentage}: {IGIBPPercentage: number}): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Note que en base a la General Resolution RG 4815/2020 se agregará un {IGIBPPercentage}% de Ganancias al valor de esta compra.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function TooltipIIBB(): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Note que, en base a la reglamentación del Impuesto a los Ingresos Brutos, se puede incluir un porcentaje variable entre 1% e 13.2% sobre el valor de la transacción en pesos, dependiendo del tipo de bienes / servicios adquiridos y su provincia.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function TooltipPyIVA(): Node {
  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <p>Note que en base a la Ley N 6.380 de 2019 y sus decretos, se agregará 10% de IVA al valor de esta compra.</p>
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

export function Tooltip(): Node {

  return (
    <TooltipStyled>
      <TooltipBadge>?</TooltipBadge>
      <TooltipContainer>
        <TooltipBody>
          <TooltipMessage />
        </TooltipBody>
        <TooltipArrow />
      </TooltipContainer>
    </TooltipStyled>
  );
}

function TooltipMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = useCurrentReceipt();
  const fx = getReceiptFx(receipt);
  const decimalFormat = getMoneyFormat(getCountry(incomingPayment));
  const formatFx = fxToCurrencyString(decimalFormat);
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return (
        <p>
          A Taxa de Câmbio é o preço de uma moeda estrangeira em relação a moeda local. <br/>
            US$&nbsp;1,00, = R$&nbsp;{formatFx(fx)}
        </p>
      );

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'py':
    case 'uy':
      return (
        <p>
          El tipo de Cambio es el precio de una moneda extranjera en relación a la moneda local. <br />
          {getAmountCurrencyCode(getReceiptAmountExt(receipt))}&nbsp;1,00 = &nbsp;{formatFx(fx)}
        </p>
      );

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
      return (
        <p>
          The Exchange Rate is the price of a foreign currency against the local currency. <br />
          {getAmountCurrencyCode(getReceiptAmountExt(receipt))}&nbsp;1,00 = &nbsp;{formatFx(fx)}
        </p>
      );

    default:
      throw new Error(`No tooltip message found for country ${country}`);
  }}

const TooltipBadge = styled.div`
  background-color: #c5cad1;
  border-radius: 1em;
  color: white;
  font-size: .86em;
  font-weight: 700;
  width: 1.1em;
  height: 1.1em;
  text-align: center;
  `;

const TooltipArrow = styled.i`
  height: 12px;
  left: 22px;
  margin-left: -12px;
  overflow: hidden;
  position: absolute;
  top: 100%;
  width: 24px;

  &:after {
    background-color: #ffffff;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.18);
    content: "";
    height: 15px;
    left: 50%;
    position: absolute;
    transform: translate(-50%, -50%) rotate(45deg);
    width: 15px;
  }
  `;

const TooltipBody = styled.div`
  font-size: 12px;
  font-weight: normal;
`;

const TooltipContainer = styled.div`
  background-color: #ffffff;
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, .18);
  box-sizing: border-box;
  font-family: ${props => props.theme.fontFamily.sansSerif};
  color: #738093;
  left: 46px;
  min-width: 170px;
  opacity: 0;
  padding: 6px 10px;
  position: absolute;
  top: -1em;
  z-index: 99999999;
  `;

const TooltipStyled = styled.div`
  cursor: pointer;
  display: inline-block;
  position: relative;
  margin-left: 5px;

  ${TooltipContainer} {
    transition: 200ms all ease-in;
    transform: translate(-30%, -110%);
    opacity: 0;
    visibility: hidden;
  }

  &:hover ${TooltipContainer} {
    transform: translate(-30%, -100%);
    opacity: 1;
    visibility: visible;
  }
  `;
